<template>
  <div class="feedback-section" v-if="isVisible">
    <!-- Etat initial : boutons -->
    <div v-if="!submitted" class="feedback-row">
      <span class="feedback-label">{{ $t('modals.feedback.ratingLabel') }}</span>
      <div class="feedback-buttons">
        <button
          class="feedback-btn"
          :class="{ active: selectedRating === 'positive' }"
          :disabled="submitting"
          @click="selectRating('positive')"
          :title="$t('modals.feedback.useful')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.56 2a2.5 2.5 0 0 1 2.452 2.99L11.609 7h2.235a2.75 2.75 0 0 1 2.713 3.202l-.681 4.082A3.25 3.25 0 0 1 12.67 17H4.5A1.5 1.5 0 0 1 3 15.5V9.238a1.5 1.5 0 0 1 1.059-1.433l1.14-.35.139-.048a2.75 2.75 0 0 0 1.56-1.453L8.41 2.59l.07-.13A1 1 0 0 1 9.322 2zM7.81 6.365a3.75 3.75 0 0 1-2.126 1.98l-.192.065-1.14.35A.5.5 0 0 0 4 9.239V15.5a.5.5 0 0 0 .5.5h8.17a2.25 2.25 0 0 0 2.22-1.88l.68-4.082A1.75 1.75 0 0 0 13.844 8H11a.5.5 0 0 1-.49-.598l.521-2.608A1.5 1.5 0 0 0 9.561 3h-.238z"/>
          </svg>
        </button>
        <button
          class="feedback-btn"
          :class="{ active: selectedRating === 'negative' }"
          :disabled="submitting"
          @click="selectRating('negative')"
          :title="$t('modals.feedback.notUseful')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
            <path d="M12.67 3a3.25 3.25 0 0 1 3.206 2.716l.68 4.082A2.75 2.75 0 0 1 13.845 13H11.61l.403 2.01A2.5 2.5 0 0 1 9.56 18h-.238a1 1 0 0 1-.843-.46l-.069-.13-1.514-3.364a2.75 2.75 0 0 0-1.56-1.453l-.139-.047-1.14-.35A1.5 1.5 0 0 1 3 10.761V4.5A1.5 1.5 0 0 1 4.5 3zM4.5 4a.5.5 0 0 0-.5.5v6.262a.5.5 0 0 0 .353.477l1.14.35.19.065a3.75 3.75 0 0 1 2.127 1.98L9.323 17h.238a1.5 1.5 0 0 0 1.47-1.794l-.521-2.608A.5.5 0 0 1 11 12h2.844a1.75 1.75 0 0 0 1.726-2.038l-.68-4.082A2.25 2.25 0 0 0 12.67 4z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Champ commentaire (thumbs down) -->
    <div v-if="selectedRating === 'negative' && !submitted" class="feedback-comment-section">
      <textarea
        v-model="comment"
        :placeholder="$t('modals.feedback.commentPlaceholder')"
        class="feedback-textarea"
        rows="2"
        maxlength="2000"
      ></textarea>
      <button class="feedback-submit-btn" :disabled="submitting || !comment.trim()" @click="submitFeedback">
        <span v-if="!submitting">{{ $t('common.send') }}</span>
        <span v-else class="feedback-spinner"></span>
      </button>
    </div>

    <!-- Etat confirme -->
    <div v-if="submitted" class="feedback-confirmed">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
        <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z"/>
      </svg>
      <span>{{ $t('modals.feedback.thankYou') }}</span>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

export default {
  name: 'FeedbackModal',
  props: {
    isVisible: { type: Boolean, default: true },
    dossierId: { type: String, required: true },
    dossierMode: { type: String, default: null },
    // Index du tour noté (mode agent multi-tours). null = feedback global au dossier (DossierPage).
    turnIdx: { type: Number, default: null },
  },
  data() {
    return {
      selectedRating: null,
      comment: '',
      submitting: false,
      submitted: false,
    };
  },
  methods: {
    async selectRating(rating) {
      this.selectedRating = rating;
      // Thumbs up : soumission immediate
      if (rating === 'positive') {
        await this.submitFeedback();
      }
    },
    async submitFeedback() {
      if (this.submitting || !this.selectedRating) return;
      this.submitting = true;

      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error('Session expiree');

        const response = await fetch(`${API_URL}/copilot/submit-feedback`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dossier_id: this.dossierId,
            rating: this.selectedRating,
            comment: this.comment.trim() || null,
            dossier_mode: this.dossierMode,
            turn_idx: this.turnIdx,
          }),
        });

        if (!response.ok) throw new Error('Erreur serveur');

        this.submitted = true;
      } catch (err) {
        console.error('Feedback submission error:', err);
        this.$toast?.error?.(this.$t('modals.feedback.toasts.submitError'));
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.feedback-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.feedback-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.feedback-label {
  font-size: 0.82rem;
  color: #888;
}

.feedback-buttons {
  display: flex;
  gap: 4px;
}

.feedback-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #999;
  cursor: pointer;
  transition: all 0.15s ease;
}

.feedback-btn:hover:not(:disabled) {
  border-color: #aaa;
  color: #555;
  background: #f5f5f5;
}

.feedback-btn.active {
  border-color: #667eea;
  color: #667eea;
  background: #f0f0ff;
}

.feedback-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.feedback-comment-section {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feedback-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.82rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
}

.feedback-textarea:focus {
  border-color: #667eea;
}

.feedback-submit-btn {
  align-self: flex-end;
  padding: 5px 14px;
  font-size: 0.8rem;
  border: none;
  border-radius: 5px;
  background: #667eea;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
}

.feedback-submit-btn:hover:not(:disabled) {
  background: #5a6fd6;
}

.feedback-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.feedback-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.feedback-confirmed {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: #52c41a;
}
</style>
